import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselApi,
} from "@/components/ui/carousel";
import { useCLAProgress } from "@/contexts/CLAProgressContext";
import { cn } from "@/lib/utils";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export function GlobalCLAProgressDrawer() {
  const {
    isOpen,
    projectName,
    steps,
    currentStepIndex,
    overallStatus,
    statusMessage,
    closeProgress,
    setCurrentStepIndex,
  } = useCLAProgress();

  const [api, setApi] = useState<CarouselApi>();
  const hasAutoScrolled = useRef(false);

  // Auto-scroll to running step when steps change
  useEffect(() => {
    if (!api) return;
    
    // Find the currently running step
    const runningIndex = steps.findIndex(s => s.status === 'running');
    
    // If there's a running step and it's different from current view, scroll to it
    if (runningIndex >= 0 && runningIndex !== api.selectedScrollSnap()) {
      api.scrollTo(runningIndex);
    }
  }, [api, steps]);

  // Track carousel slide changes and sync with dots
  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      const selectedIndex = api.selectedScrollSnap();
      setCurrentStepIndex(selectedIndex);
    };

    api.on("select", onSelect);
    api.on("reInit", onSelect);
    
    // Set initial position
    onSelect();
    
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api, setCurrentStepIndex]);

  // Reset auto-scroll flag when dialog opens
  useEffect(() => {
    if (isOpen) {
      hasAutoScrolled.current = false;
    }
  }, [isOpen]);

  const getStatusIcon = () => {
    switch (overallStatus) {
      case 'running':
        return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
      case 'completed':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-destructive" />;
      default:
        return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;
    }
  };

  const getStepDotColor = (status: string, isActive: boolean) => {
    const baseClasses = "w-3 h-3 rounded-full transition-all duration-300";
    
    if (isActive) {
      switch (status) {
        case 'completed':
          return cn(baseClasses, "bg-green-500 ring-2 ring-green-500/30 ring-offset-2 ring-offset-background");
        case 'running':
          return cn(baseClasses, "bg-primary animate-pulse ring-2 ring-primary/30 ring-offset-2 ring-offset-background");
        case 'failed':
          return cn(baseClasses, "bg-destructive ring-2 ring-destructive/30 ring-offset-2 ring-offset-background");
        default:
          return cn(baseClasses, "bg-muted-foreground/30 ring-2 ring-muted-foreground/10 ring-offset-2 ring-offset-background");
      }
    }

    switch (status) {
      case 'completed':
        return cn(baseClasses, "bg-green-500");
      case 'running':
        return cn(baseClasses, "bg-primary animate-pulse");
      case 'failed':
        return cn(baseClasses, "bg-destructive");
      case 'skipped':
        return cn(baseClasses, "bg-muted-foreground/20");
      default:
        return cn(baseClasses, "bg-muted-foreground/30");
    }
  };

  if (!isOpen || steps.length === 0) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeProgress()}>
      <DialogContent 
        className="max-w-lg max-h-[85vh] flex flex-col p-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Header */}
        <DialogHeader className="flex-shrink-0 border-b p-4">
          <div className="flex items-center gap-3">
            {getStatusIcon()}
            <div>
              <DialogTitle className="text-lg font-semibold">
                {statusMessage}
              </DialogTitle>
              <p className="text-sm text-muted-foreground mt-0.5">{projectName}</p>
            </div>
          </div>
        </DialogHeader>

        {/* Main Content */}
        <div className="flex p-6 gap-6">
          {/* Current Step Display */}
          <div className="flex-1 flex flex-col items-center">
            <Carousel 
              orientation="vertical"
              className="w-full"
              setApi={setApi}
              opts={{
                align: 'start',
                loop: false,
                containScroll: 'trimSnaps',
              }}
            >
              <CarouselContent className="h-[280px]">
                {steps.map((step, index) => (
                  <CarouselItem key={step.name} className="basis-full">
                    <div className="h-full flex flex-col items-center justify-center px-4">
                      {/* Step Image */}
                      <div className="w-full max-w-xs aspect-video rounded-xl overflow-hidden bg-muted mb-4 shadow-lg">
                        <img 
                          src={step.image} 
                          alt={step.label}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      {/* Step Info */}
                      <div className="text-center space-y-1">
                        <div className="flex items-center justify-center gap-2">
                          {step.status === 'running' && (
                            <Loader2 className="h-4 w-4 animate-spin text-primary" />
                          )}
                          {step.status === 'completed' && (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          )}
                          {step.status === 'failed' && (
                            <XCircle className="h-4 w-4 text-destructive" />
                          )}
                          <h3 className="text-lg font-semibold">
                            Step {index + 1}: {step.label}
                          </h3>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {step.description || 'Pending'}
                        </p>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>

          {/* Vertical Dot Indicators */}
          <div className="flex flex-col justify-center gap-3">
            {steps.map((step, index) => (
              <button
                key={step.name}
                onClick={() => api?.scrollTo(index)}
                className={getStepDotColor(step.status, currentStepIndex === index)}
                aria-label={`Go to step ${index + 1}: ${step.label}`}
              />
            ))}
          </div>
        </div>

        {/* Footer with step counter */}
        <div className="flex-shrink-0 border-t p-4">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              Step {currentStepIndex + 1} of {steps.length}
            </span>
            <span>
              {steps.filter(s => s.status === 'completed').length} completed
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
